import type { ColumnDef } from '@tanstack/vue-table'
import { HTTP_STATUS_CODES } from './Consts'
import type { GroupsWithColumns } from './tables'
import { ConnectedAccounts } from './tables/ConnectedAccounts'
import type { BaseQueryArgs, ErrorObject, GroupingArgs, SearchQuery } from './types/types'
import { getQueryStringFromFiltersObject } from './utils'

export function createFormatter<T>(fn?: (data: T) => T): (data: T) => T {
  return fn ?? ((data) => data)
}

export function getBasicUnauthorizedResponse(): ErrorObject {
  return {
    statusCode: HTTP_STATUS_CODES.UNAUTHORIZED,
    httpError: 'Unauthorized',
    error: 'Unauthorized',
    isUnauthorized: true,
  }
}

export function injectUrlWithParams(url: string, params: Record<string, string | number>): string {
  let urlWithParams = url
  for (const [key, value] of Object.entries(params)) {
    // This replaces the path arguments with the correct values
    // e.g. /api/v1/{id} => /api/v1/1
    urlWithParams = urlWithParams.replace(`{${key}}`, String(value))
  }
  return urlWithParams
}

export function formatMoney(amount: number, isMoney: boolean = true): string {
  if (!isMoney)
    return amount?.toLocaleString('nb-NO', {
      currency: 'NOK',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })
  return amount?.toLocaleString('nb-NO', {
    style: 'currency',
    currency: 'NOK',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })
}

export function deepCompare(a: any, b: any) {
  if (a === b) return true
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false
    const sortedA = a.slice().sort()
    const sortedB = b.slice().sort()
    for (let i = 0; i < sortedA.length; i++) {
      if (!deepCompare(sortedA[i], sortedB[i])) return false
    }
    return true
  }

  if (typeof a !== 'object' || typeof b !== 'object') return false
  if (Object.keys(a).length !== Object.keys(b).length) return false
  for (const key in a) {
    if (!deepCompare(a[key], b[key])) return false
  }

  return true
}

/*
Environment URLs:

prod:
- https://portal.paybucky.com
- https://portal.prod.paybucky.com
- https://internal.paybucky.com
- https://internal.prod.paybucky.com

test:
- https://portal.test.paybucky.com
- https://internal.test.paybucky.com

staging:
- https://portal.staging.paybucky.com
- https://internal.staging.paybucky.com

dev:
- https://portal.dev.paybucky.com
- https://internal.dev.paybucky.com
*/
export function getSentryEnvironment(url: string): string | undefined {
  const match = url.match(/^https:\/\/([^.]+)(?:\.([^.]+))?\.paybucky\.com$/)
  if (!match) return
  return match[2] ?? 'prod'
}

export function getQueryStringFromGroupingObject<T>(grouping: GroupingArgs<T>): string {
  let queryString = Object.entries(grouping.aggregateFunctions ?? {})
    .map(([fun, columns]) => {
      if (columns === undefined) return ''
      return `${fun}=${columns.filter((x) => x).join(',')}`
    })
    .filter((x) => x)
    .join(';')

  if (grouping.groupBy) {
    if (queryString !== '') {
      queryString += ';'
    }
    queryString += `groupBy=${grouping.groupBy.join(',')}`
  }

  return queryString
}

export function mergeBaseQueryArgs<T>(
  target: Partial<BaseQueryArgs<T>>,
  source: Record<string, string>
): Record<string, any> {
  return {
    ...target,
    filters: getQueryStringFromFiltersObject(
      (target as { filters: SearchQuery<unknown> })?.filters ?? {}
    ),
    grouping: getQueryStringFromGroupingObject(
      (target as { grouping: GroupingArgs<unknown> })?.grouping ?? {}
    ),
    ...source,
  }
}

export function keepKeys<T extends Object>(obj: T, keys: string[]): Partial<T> {
  const result: Partial<T> = {}
  keys.forEach((key) => {
    if (key in obj) {
      result[key] = obj[key]
    }
  })
  return result
}

export function keepKeysToArray<T extends Object>(obj: T, keys: (keyof T)[]): T[keyof T][] {
  const result: T[keyof T][] = []
  keys.forEach((key) => {
    if (key in obj) {
      result.push(obj[key])
    }
  })
  return result
}

export function filterIncludeColumns<L>(
  obj: GroupsWithColumns<L>[keyof GroupsWithColumns<L>],
  keys: (keyof L)[]
): ColumnDef<L>[] {
  const result: ColumnDef<L>[] = []

  obj.columns.forEach((column) => {
    if (keys.includes((column as any).accessorKey)) {
      result.push(column)
    }
  })
  return result
}

export function filterExcludeColumns<L>(
  obj: GroupsWithColumns<L>[keyof GroupsWithColumns<L>],
  keys: (keyof L)[]
): ColumnDef<L>[] {
  const result: ColumnDef<L>[] = []

  obj.columns.forEach((column) => {
    if (!keys.includes((column as any).accessorKey)) {
      result.push(column)
    }
  })
  return result
}

export function shallowTypeEquals(obj1: any, obj2: any): boolean {
  const keys1 = Object.keys(obj1)
  const keys2 = Object.keys(obj2)

  // Check if both objects have the same number of properties
  if (keys1.length !== keys2.length) {
    return false
  }

  // Iterate through the properties of the first object
  for (const key of keys1) {
    // Check if both objects contain the current property
    if (!(key in obj2)) {
      return false
    }

    // Compare the types of the corresponding properties
    if (obj1[key] !== obj2[key]) {
      return false
    }
  }

  // If all properties match in type, return true
  return true
}

/**
 * @deprecated Use `usePejer().getDefaultColumns` instead
 */
export function getDefaultColumn(table: keyof typeof defaultColumns) {
  return defaultColumns[table]
}

export const defaultColumns = {
  payments: {
    sortingColumn: 'insertTime',
    id: 'paymentReferenceID',
  },
  partialPayments: {
    sortingColumn: 'insertTime',
    id: 'paymentReferenceID',
  },
  invoices: {
    sortingColumn: 'insertTime',
    id: 'paymentReferenceID',
  },
  customers: {
    sortingColumn: 'customerId',
    id: 'customerId',
  },
  payouts: {
    sortingColumn: 'insertTime',
    id: 'payoutId',
  },
  connectedAccounts: {
    sortingColumn: 'insertTime',
    id: 'connectedAccountID',
  },
  platforms: {
    sortingColumn: 'insertTime',
    id: 'platformID',
  },
  refunds: {
    sortingColumn: 'insertTime',
    id: 'id',
  },
  users: {
    sortingColumn: 'insertTime',
    id: 'userId',
  },
  personsOfInterest: {
    sortingColumn: 'id',
    id: 'id',
  },
  businesses: {
    sortingColumn: 'id',
    id: 'id',
  },
}

export function downloadFile(
  fileContents: string,
  contentType: string,
  fileDownloadName: string
): void {
  // Decode the file contents (assuming it's base64-encoded)
  const byteCharacters = atob(fileContents)
  const byteNumbers = new Array(byteCharacters.length)
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i)
  }
  const byteArray = new Uint8Array(byteNumbers)
  const blob = new Blob([byteArray], { type: contentType })

  // Create a link element to download the file
  const downloadLink = document.createElement('a')
  const url = window.URL.createObjectURL(blob)
  downloadLink.href = url

  // Set the file name
  downloadLink.download = fileDownloadName

  // Append the link to the document body and click it to start the download
  document.body.appendChild(downloadLink)
  downloadLink.click()

  // Clean up: remove the link element
  document.body.removeChild(downloadLink)
  window.URL.revokeObjectURL(url)
}

function fillMissingDates(startDate: Date, endDate: Date, existingDates: Date[]): Date[] {
  const dateSet = new Set(existingDates.map((date) => date.toISOString().slice(0, 10)))
  const currentDate = new Date(startDate.toISOString().slice(0, 10))
  const allDates: Date[] = []

  while (currentDate <= endDate) {
    if (!dateSet.has(currentDate.toISOString().slice(0, 10))) {
      const d = new Date(currentDate)
      d.setDate(d.getDate() + 1)
      allDates.push(d)
    }
    currentDate.setDate(currentDate.getDate() + 1) // Move to the next day
  }

  return allDates
}

export function padDate(data: ConnectedAccounts.Statistics[], startDate: Date, endDate: Date) {
  if (data.length === 0) return data

  const sortedData = [...data].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
  )

  const missingDates = fillMissingDates(
    floorDays(startDate),
    floorDays(endDate),
    sortedData.map((x) => new Date(x.date))
  )

  for (const date of missingDates) {
    sortedData.push({
      date: date.toISOString(),
      paymentCount: 0,
      revenueSum: 0,
      paidOutSum: 0,
      paidOutCount: 0,
    })
  }

  sortedData.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())

  return sortedData
}

export function floorDays(date: Date) {
  date.setHours(0, 0, 0)
  return date
}
